import React, { useCallback, useEffect, useState } from "react";
import { Form, Field } from "react-final-form";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { TextInput } from "../components/inputs/final_form/TextInput";
import { DateInput } from "../components/inputs/final_form/DateInput";
import { SelectInput } from "../components/inputs/final_form/SelectInput";
import { PillToggleInput } from "../components/inputs/final_form/PillToggleInput";
import { required } from "../util/validators";
import { LoadingButton } from "../components/LoadingButton";
import { api, extractErrorMessage } from "../util/api";
import { DropzoneInput } from "../components/inputs/final_form/DropzoneInput";
import { DetailedErrorAlert } from "../components/DetailedErrorAlert";
import { omit } from "lodash-es";

const statusOptions = [
  {
    label: "Rejected",
    value: "rejected",
  },
  {
    label: "Approved",
    value: "approved",
  },
  {
    label: "Denied",
    value: "denied",
  },
  {
    label: "Documented Clean Claim",
    value: "documented_clean_claim",
  },
];

const LabStatusCaseForm = ({ onSubmit, denialReasonCodeOptions }) => {
  return (
    <Form onSubmit={onSubmit}>
      {({ handleSubmit, submitting, values, form }) => (
        <>
          <Field
            label={<span className="fw-bold">New Case Status</span>}
            component={PillToggleInput}
            name="status"
            options={statusOptions}
            validate={required}
          />

          {/* If status is "documented_clean_claim" or "rejected" no extra fields needed. */}

          {/* Approved Fields */}
          {values.status === "approved" && (
            <>
              <Field
                component={TextInput}
                label="Authorization Number"
                name="submission_records_attributes[0].reference_number"
                validate={required}
                required
              />
              <Field
                component={DateInput}
                label="Authorization Date"
                name="submission_records_attributes[0].submitted_at"
                validate={required}
                required
              />
              <Field component={DateInput} label="Authorization Effective Date" name="authorization_effective_date" />
              <Field component={DateInput} label="Authorization Expiration Date" name="authorization_expiration_date" />
            </>
          )}

          {/* Denied Fields */}
          {values.status === "denied" && (
            <>
              <Field component={TextInput} label="Reference Number" name="submission_records_attributes[0].reference_number" />
              <Field component={SelectInput} label="Denial Reason Code" name="denial_codes" options={denialReasonCodeOptions} />

              {values.denial_codes === "0" && (
                <Field component={TextInput} label="Denial Reason" name="reason" validate={required} required />
              )}
            </>
          )}

          {/* Show the comment and attachment fields after the user has selected a status */}
          {values.status && (
            <>
              <Field component={TextInput} label="Comment" name="comment" validate={required} required />

              <Field name="additional_documents" component={DropzoneInput} multiple />

              <LoadingButton onClick={handleSubmit} loading={submitting}>
                Update Case
              </LoadingButton>
            </>
          )}
        </>
      )}
    </Form>
  );
};

export const LabStatusCaseModal = ({ caseId, denialReasonCodeOptions, buttonEl }) => {
  const [error, setError] = useState(null);

  const onSubmit = useCallback(
    async (data) => {
      setError(null);

      try {
        // Create copy of the PA attributes without any documents
        const attributes = omit(data, ["additional_documents", "comment"]);

        if (attributes.status === "rejected") {
          attributes.substatus = "per_lab_request";
        } else if (attributes.status === "denied" && attributes.denial_codes !== "0") {
          attributes.reason = denialReasonCodeOptions.find((option) => option.value === attributes.denial_codes)?.label;
        }

        await api.put(`/cases/${caseId}/mark_case_as_closed`, {
          case: {
            pa_request_attributes: attributes,
          },
          requestAttachments: {
            newFiles: data.additional_documents,
          },
          comment: data.comment,
        });

        window.location.reload();
      } catch (err) {
        console.error(err);
        setError(extractErrorMessage(err));
      }
    },
    [caseId, denialReasonCodeOptions]
  );

  const [isOpen, setIsOpen] = useState(false);
  const toggleModal = useCallback(() => setIsOpen((wasOpen) => !wasOpen), []);

  useEffect(() => {
    buttonEl.addEventListener("click", toggleModal);

    return () => {
      buttonEl.removeEventListener("click", toggleModal);
    };
  }, [buttonEl, toggleModal]);

  return (
    <Modal isOpen={isOpen} toggle={toggleModal}>
      <ModalHeader toggle={toggleModal}>Status and Close Case</ModalHeader>

      <ModalBody>
        <DetailedErrorAlert error={error} className="mb-3" />

        <LabStatusCaseForm denialReasonCodeOptions={denialReasonCodeOptions} onSubmit={onSubmit} />
      </ModalBody>
    </Modal>
  );
};
